import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { casinoBetListService } from "../../../services/admin/casinoBetList.service";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";

export type payloadCasinoList = {
  playerId: string | number | undefined;
  payload: {
    roundId?: string;
    productType?: string;
    gameProviderName?: string;
    gameName?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number | '';
    after?: string;
  }
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  items: listItem[];
  columns: columnsItem[];
  totalCount: number | string;
}

export type casinoBetListSliceState = {
  casino: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  filters: {
    data: any,
    error: any,
    isFetching: boolean,
  },
};

const initialState: casinoBetListSliceState = {
  casino: {
    data: null,
    error: null,
    isFetching: false,
  },
  filters: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getCasinoListData: any = createAsyncThunk(
  'admin/casinoBetList/getCasinoListData',
  async (payload: payloadCasinoList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await casinoBetListService.getCasinoList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());

        if (data?.error === 'common.errors.agent.bet-history.unable_to_retrieve_bet_history') {
          dispatch(setGeneralSnackbarState({
            open: true,
            messageKey: 'common.errors.agent.bet-history.unable_to_retrieve_bet_history',
            type: 'error'
          }));
        }

        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCasinoBetListFiltersData: any = createAsyncThunk(
  'admin/casinoBetList/getCasinoBetListFiltersData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await casinoBetListService.getCasinoBetListFilters();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const casinoBetListSlice = createSlice({
  name: 'casinoBetList',
  initialState: initialState,
  reducers: {
    clearCasinoListState(state) {
      state.casino.error = null;
      state.casino.isFetching = false;
      state.casino.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<casinoBetListSliceState>>) => {
    // Get Casino List
    builder.addCase(getCasinoListData.pending, (state ) => {
      state.casino.isFetching = true;
      state.casino.error = null;
    });
    builder.addCase(getCasinoListData.fulfilled, (state , action) => {
      state.casino.error = null;
      state.casino.isFetching = false;

      const hasAfter = action.meta?.arg?.payload.hasOwnProperty('after') && action.meta?.arg?.payload?.after

      if (hasAfter && state.casino.data) {
        state.casino.data.items = [
          ...(state.casino.data.items || []),
          ...(action.payload.items || []),
        ];
      } else {
        state.casino.data = action.payload;
      }
    });
    builder.addCase(getCasinoListData.rejected, (state , action) => {
      state.casino.isFetching = false;
      state.casino.error = action.payload;
      state.casino.data = null;
    });

    // Get Casino Filters
    builder.addCase(getCasinoBetListFiltersData.pending, (state ) => {
      state.filters.isFetching = true;
      state.filters.error = null;
    });
    builder.addCase(getCasinoBetListFiltersData.fulfilled, (state , action) => {
      state.filters.error = null;
      state.filters.isFetching = false;
      state.filters.data = action.payload;
    });
    builder.addCase(getCasinoBetListFiltersData.rejected, (state , action) => {
      state.filters.isFetching = false;
      state.filters.error = action.payload;
      state.filters.data = null;
    });
  },
});

export const {
  clearCasinoListState,
} = casinoBetListSlice.actions;

export default casinoBetListSlice.reducer;
